import { CatList } from '@blocks-react/components/List';
import { CatListItem } from '@blocks-react/components/ListItem';
import { CatHeading } from '@blocks-react/components/Heading';
import { PAGE_BREAKPOINTS } from '../../../constants/commonConstants';
import { useBreakpoint } from '../../../hooks';
import { Conditional } from '../../common';
import { CategoriesList } from './CategoriesList';
import cx from 'classnames';
import { BuyingOptions } from './BuyingOptions';
import { CategoryOrBuyingOption, ProductCategoriesType } from '../types';
import { MAIN_NAV_TRACK } from '../../../constants/analyticsConstants';
import styles from './style.module.scss';

export const ProductCategories = ({
  categoriesList = [],
  buyingOptions = [],
  isCatCard,
  translations,
  url = '',
  isMobile,
  topOffset = 137,
  isCookieAccepted=true,
}: ProductCategoriesType) => {
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.BLOCKS_LG);

  const handleRedirect = (e: Event, seoUrlKeyword: string | undefined) => {
    e.stopPropagation();
    if (seoUrlKeyword) {
      window.location.href = seoUrlKeyword;
    }
  };

  return (
    <CatList
      spacing="padded"
      trigger="click"
      className={cx(
        isMobile
          ? styles['category-wrapper_mobile']
          : styles['category-wrapper']
      )}
      style={{
        ['--cat-list-height' as string]: `calc(100vh - ${topOffset}px - 16px)`
      }}
      singleOpen
      data-testid="lucid-product-category"
    >
      <CatListItem
        isStatic
        aria-label={translations?.heading}
        className={styles['title-padding']}
        data-track-event={MAIN_NAV_TRACK.EVENT}
        data-track-attr-nltext={'Parts'}
        data-track-attr-context={MAIN_NAV_TRACK.TOP_SUBMENU_CONTEXT}
      >
        <CatHeading
          variant="display-sm"
          color={!isMD ? 'knockout' : undefined}
          data-testid="shop-title"
        >
          {translations?.heading}
        </CatHeading>
      </CatListItem>

      <CatListItem
        onBlSelect={(e: Event) => {
          handleRedirect(e, `${url}/ShopAllCategories`);
        }}
        aria-label={translations?.subHeading}
        data-track-event={MAIN_NAV_TRACK.EVENT}
        data-testid="See-all-categories"
        data-track-attr-nltext={'See All Categories'}
        data-track-attr-context={MAIN_NAV_TRACK.TOP_SUBMENU_CONTEXT}
      >
        <label
          className={cx(styles['subheading'], {
            [styles['subheading__mobile']]: !isMD
          })}
          data-testid="label"
        >
          {translations?.subHeading}
        </label>
      </CatListItem>
      {categoriesList.map((category: CategoryOrBuyingOption) => {
        return (
          <CatListItem
            key={category.name}
            aria-label={category.name}
            onBlSelect={(e: Event) => {
              !category.nestedCategories?.length &&
                handleRedirect(e, category?.seoUrlKeyword);
            }}
            data-track-event={MAIN_NAV_TRACK.EVENT}
            data-track-attr-nltext={category?.categoryIdentifier}
            data-track-attr-context={MAIN_NAV_TRACK.TOP_SUBMENU_CONTEXT}
          >
            {category.name}
            <Conditional test={!!category.nestedCategories?.length}>
              <CatList slot="items" trigger="click">
                <CategoriesList
                  heading={category.name}
                  subHeading={category.name}
                  seoUrlKeyword={category.seoUrlKeyword}
                  handleRedirect={handleRedirect}
                  isMD={!!isMD}
                  subTitle={translations?.subTitle}
                  categoryIdentifier={category.categoryIdentifier}
                />
                {category.nestedCategories?.map(
                  (subCategory: CategoryOrBuyingOption) => (
                    <CatListItem
                      key={subCategory.name}
                      aria-label={subCategory.name}
                      onBlSelect={(e: Event) => {
                        !subCategory.nestedCategories?.length &&
                          handleRedirect(e, subCategory?.seoUrlKeyword);
                      }}
                      data-track-event={MAIN_NAV_TRACK.EVENT}
                      data-track-attr-nltext={subCategory?.categoryIdentifier}
                      data-track-attr-context={
                        MAIN_NAV_TRACK.TOP_SUBMENU_CONTEXT
                      }
                    >
                      {subCategory.name}
                      <Conditional
                        test={!!subCategory?.nestedCategories?.length}
                      >
                        <CatList slot="items" trigger="click">
                          <CategoriesList
                            heading={subCategory.name}
                            subHeading={subCategory.name}
                            seoUrlKeyword={subCategory.seoUrlKeyword}
                            handleRedirect={handleRedirect}
                            isMD={!!isMD}
                            subTitle={translations?.subTitle}
                            categoryIdentifier={category.categoryIdentifier}
                          />
                          {subCategory?.nestedCategories?.map(
                            (subSubCategory: CategoryOrBuyingOption) => (
                              <CatListItem
                                key={subSubCategory.name}
                                aria-label={subSubCategory?.name}
                                onBlSelect={(e: Event) => {
                                  !subSubCategory.nestedCategories?.length &&
                                    handleRedirect(
                                      e,
                                      subSubCategory?.seoUrlKeyword
                                    );
                                }}
                                data-track-event={MAIN_NAV_TRACK.EVENT}
                                data-track-attr-nltext={
                                  subSubCategory?.categoryIdentifier
                                }
                                data-track-attr-context={
                                  MAIN_NAV_TRACK.TOP_SUBMENU_CONTEXT
                                }
                              >
                                {subSubCategory.name}
                              </CatListItem>
                            )
                          )}
                        </CatList>
                      </Conditional>
                    </CatListItem>
                  )
                )}
              </CatList>
            </Conditional>
          </CatListItem>
        );
      })}
      <BuyingOptions
        heading={translations?.buyingOptionLabel}
        list={buyingOptions}
        isMobile={!isMD}
        isCatCard={isCatCard}
        ssoLabel={translations?.ssoLabel}
        catCardMenuLabel={translations?.catCardMenuLabel}
        baseUrl={url}
      />
    </CatList>
  );
};
